import { Menu, Transition } from '@headlessui/react';
import {
  ChevronUpIcon,
  CollectionIcon,
  DownloadIcon,
  HeartIcon as HeartOutlineIcon,
  ReplyIcon,
  TrashIcon,
  XIcon,
} from '@heroicons/react/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import {
  Fragment,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import clsxm from '@/lib/clsxm';
import {
  DOWNLOAD_BATCH_MAX_ITEMS,
  formatBytes,
  getMediaDownloadUrl,
  isMediaFailed,
  isMediaOwnedBy,
  isMediaSelectable,
} from '@/lib/mediaLibrary/mediaItems';

import Spinner from '@/components/Spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';

import {
  type MediaLibraryTask,
  useMediaSelectedItems,
  useMediaSelection,
  useMediaSelectionActive,
  useMediaSelectionCount,
} from './MediaSelectionProvider';

const isEditableTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  if (!element) return false;
  return (
    element.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
  );
};

const isModalOpen = () =>
  typeof document !== 'undefined' &&
  Boolean(document.querySelector('[role="dialog"][aria-modal="true"]'));

const isMac = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const BarButton = ({
  children,
  onClick,
  disabled,
  tone = 'ghost',
  className,
  label,
  tooltip,
  buttonRef,
  autoFocus,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'ghost' | 'primary' | 'danger' | 'dangerGhost';
  className?: string;
  label?: string;
  tooltip?: string;
  buttonRef?: React.Ref<HTMLButtonElement>;
  autoFocus?: boolean;
}) => {
  const button = (
    <button
      ref={buttonRef}
      type='button'
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      autoFocus={autoFocus}
      className={clsxm(
        'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-40 sm:h-10 sm:px-3',
        tone === 'ghost' &&
          'text-gray-200 hover:bg-white/10 hover:text-white active:bg-white/15',
        tone === 'primary' &&
          'bg-white text-gray-900 shadow-sm hover:bg-gray-100 active:bg-gray-200',
        tone === 'danger' &&
          'bg-red-500 text-white shadow-sm hover:bg-red-600 active:bg-red-700',
        tone === 'dangerGhost' &&
          'text-gray-200 hover:bg-red-500/15 hover:text-red-300 active:bg-red-500/25',
        className,
      )}
    >
      {children}
    </button>
  );
  if (!tooltip) return button;
  return <SimpleTooltip title={tooltip}>{button}</SimpleTooltip>;
};

const Divider = () => (
  <span
    aria-hidden='true'
    className='mx-1 hidden h-6 w-px shrink-0 bg-white/15 sm:block'
  />
);

// Bars currently on screen; while any is, bottom toasts are lifted above it
// (see the data-media-selection-bar rule in globals.css).
let visibleBars = 0;

const percent = (done: number, total: number) =>
  total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

/**
 * Floating action bar for the media library's multi-select. Rendered by
 * MediaSelectionProvider; centred over the provider's anchor element so it
 * never covers a side panel (e.g. the /ai-images prompt form).
 */
const MediaSelectionBar = () => {
  const { t } = useLocale('media-library');
  const context = useMediaSelection();
  const count = useMediaSelectionCount();
  const active = useMediaSelectionActive();
  const selectedItems = useMediaSelectedItems();
  const [mounted, setMounted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; width: number } | null>(
    null,
  );
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const refocusDeleteRef = useRef(false);

  useEffect(() => setMounted(true), []);

  // After "Cancel" in the confirm step, put focus back on Delete (it only
  // exists again once the idle bar has rendered).
  useEffect(() => {
    if (!confirming && refocusDeleteRef.current) {
      refocusDeleteRef.current = false;
      deleteButtonRef.current?.focus();
    }
  }, [confirming]);

  const cancelConfirm = () => {
    refocusDeleteRef.current = true;
    setConfirming(false);
  };

  const task = context?.task ?? null;
  const mode = context?.mode ?? 'library';
  const visibleItems = context?.visibleItems;
  const anchorRef = context?.anchorRef;

  const visibleSelectable = useMemo(
    () => (visibleItems || []).filter((item) => isMediaSelectable(item)),
    [visibleItems],
  );
  const failedVisible = useMemo(
    () => visibleSelectable.filter((item) => isMediaFailed(item)),
    [visibleSelectable],
  );

  const userId = context?.currentUserId;
  const ownCount = useMemo(
    () => selectedItems.filter((item) => isMediaOwnedBy(item, userId)).length,
    [selectedItems, userId],
  );
  const downloadableCount = useMemo(
    () => selectedItems.filter((item) => getMediaDownloadUrl(item)).length,
    [selectedItems],
  );
  const allFavorite =
    ownCount > 0 &&
    selectedItems
      .filter((item) => isMediaOwnedBy(item, userId))
      .every((item) => item.is_favorite);
  const sharedCount = count - ownCount;

  const visible = Boolean(
    context &&
      (task !== null ||
        (context.alwaysSelecting ? visibleSelectable.length > 0 : active)),
  );

  useEffect(() => {
    if (!visible) return;
    const root = document.documentElement;
    visibleBars += 1;
    root.setAttribute('data-media-selection-bar', '');
    return () => {
      visibleBars = Math.max(0, visibleBars - 1);
      if (!visibleBars) root.removeAttribute('data-media-selection-bar');
    };
  }, [visible]);

  // Leave the confirm step whenever the selection or a task changes under it.
  useEffect(() => {
    if (!ownCount || task) setConfirming(false);
  }, [ownCount, task]);

  useLayoutEffect(() => {
    const element = anchorRef?.current;
    if (!visible || !element) {
      setAnchor(null);
      return;
    }
    const update = () => {
      const rect = element.getBoundingClientRect();
      setAnchor({ left: rect.left, width: rect.width });
    };
    update();
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    observer?.observe(element);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, visible]);

  const actions = context?.actions;
  useEffect(() => {
    if (!visible || !actions) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableTarget(event.target)) return;
      if (isModalOpen()) return;
      if (event.key === 'Escape') {
        if (confirming) {
          cancelConfirm();
        } else if (!task) {
          actions.clear();
        }
        event.preventDefault();
        return;
      }
      if (task) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        actions.selectVisible();
        return;
      }
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        ownCount > 0 &&
        !confirming
      ) {
        event.preventDefault();
        setConfirming(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, actions, confirming, task, mode, ownCount]);

  if (!mounted || !context || !actions) return null;

  const shortcut = isMac() ? '⌘A' : 'Ctrl+A';
  const totalCount = context.totalCount ?? 0;
  const canSelectAllMatching =
    context.canLoadAll && totalCount > visibleSelectable.length;
  const allVisibleSelected =
    visibleSelectable.length > 0 &&
    visibleSelectable.every((item) => context.store.isSelected(item.id!));

  const renderTask = (current: MediaLibraryTask) => {
    if (current.kind === 'download') {
      const { progress } = current;
      const done = progress.filesDone;
      const total = progress.filesTotal;
      return (
        <>
          <div className='flex min-w-0 flex-1 items-center gap-3 pl-2'>
            <span className='relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10'>
              <DownloadIcon className='h-4 w-4 animate-pulse text-white' />
            </span>
            <div className='min-w-0'>
              <p className='truncate text-sm font-medium text-white'>
                {t('Downloading {{done}} of {{total}}', { done, total })}
              </p>
              <p className='truncate text-xs tabular-nums text-gray-400'>
                {formatBytes(progress.bytes)}
                {progress.part > 1 &&
                  ` · ${t('ZIP {{part}}', { part: progress.part })}`}
                {progress.failed > 0 &&
                  ` · ${t('{{count}} skipped', { count: progress.failed })}`}
              </p>
            </div>
          </div>
          <BarButton
            onClick={actions.cancelTask}
            label={t('Cancel download')}
            className='w-9 px-0 sm:w-auto sm:px-3'
          >
            <XIcon className='h-5 w-5 sm:hidden' />
            <span className='hidden sm:inline'>{t('Cancel download')}</span>
          </BarButton>
          <span
            aria-hidden='true'
            className='absolute inset-x-3 bottom-0 h-0.5 overflow-hidden rounded-full bg-white/10'
          >
            <span
              className='block h-full rounded-full bg-primary-400 transition-[width] duration-300 ease-out'
              style={{ width: `${percent(done, total)}%` }}
            />
          </span>
        </>
      );
    }

    const label =
      current.kind === 'listing'
        ? current.total
          ? t('Selecting {{loaded}} of {{total}}…', {
              loaded: current.loaded,
              total: current.total,
            })
          : t('Loading your library…')
        : current.kind === 'trash'
          ? t('Moving {{count}} items to Trash…', { count: current.count })
          : current.kind === 'restore'
            ? t('Restoring {{count}} items…', { count: current.count })
            : current.kind === 'purge'
              ? t('Deleting {{count}} items…', { count: current.count })
              : current.kind === 'favorite'
                ? t('Adding {{count}} items to Favorites…', {
                    count: current.count,
                  })
                : t('Removing {{count}} items from Favorites…', {
                    count: current.count,
                  });
    return (
      <>
        <div className='flex min-w-0 flex-1 items-center gap-3 px-2'>
          <Spinner className='h-5 w-5 shrink-0 animate-spin text-white' />
          <p className='truncate text-sm font-medium text-white'>{label}</p>
        </div>
        {current.kind === 'listing' && (
          <BarButton onClick={actions.cancelTask}>{t('Cancel')}</BarButton>
        )}
      </>
    );
  };

  // On phones the question wraps above the buttons; from sm it is one row.
  // In Trash the same step confirms deleting for good.
  const renderConfirm = () => {
    const purging = mode === 'trash';
    return (
      <div className='flex w-full flex-col gap-2 p-1 sm:flex-row sm:items-center sm:gap-1 sm:p-0'>
        <div className='flex min-w-0 flex-1 items-center gap-3 sm:pl-1'>
          <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/15'>
            <TrashIcon className='h-5 w-5 text-red-300' />
          </span>
          <div className='min-w-0'>
            <p className='text-sm font-semibold text-white sm:truncate'>
              {purging
                ? t('Delete {{count}} items forever?', { count: ownCount })
                : t('Move {{count}} items to Trash?', { count: ownCount })}
            </p>
            <p className='text-xs text-gray-400 sm:truncate'>
              {purging
                ? t("This can't be undone. Credits used on them still count.")
                : sharedCount > 0
                  ? t('{{count}} items shared by teammates will be kept.', {
                      count: sharedCount,
                    })
                  : t('You can restore them from Trash.')}
            </p>
          </div>
        </div>
        <div className='flex shrink-0 items-center justify-end gap-1'>
          <BarButton onClick={cancelConfirm}>{t('Cancel')}</BarButton>
          <BarButton
            tone='danger'
            autoFocus
            onClick={() => {
              setConfirming(false);
              void (purging
                ? actions.purgeSelected()
                : actions.trashSelected());
            }}
          >
            <TrashIcon className='h-4 w-4' />
            {purging ? t('Delete forever') : t('Move to Trash')}
          </BarButton>
        </div>
      </div>
    );
  };

  const renderSelectMenu = () => {
    const options: { key: string; label: string; onSelect: () => void }[] = [];
    if (visibleSelectable.length > 0 && !allVisibleSelected) {
      options.push({
        key: 'visible',
        label: t('All shown ({{count}})', { count: visibleSelectable.length }),
        onSelect: actions.selectVisible,
      });
    }
    if (canSelectAllMatching) {
      options.push({
        key: 'all',
        label: t('All {{count}} items', { count: totalCount }),
        onSelect: () => void actions.selectAllMatching(),
      });
    }
    if (failedVisible.length > 0) {
      options.push({
        key: 'failed',
        label: t('Failed generations ({{count}})', {
          count: failedVisible.length,
        }),
        onSelect: actions.selectFailed,
      });
    }
    if (count > 0) {
      options.push({
        key: 'clear',
        label: t('Clear selection'),
        onSelect: actions.clear,
      });
    }
    if (!options.length) return null;

    return (
      <Menu as='div' className='relative shrink-0'>
        <Menu.Button
          aria-label={t('Select all')}
          className='inline-flex h-9 items-center gap-1.5 rounded-xl px-2.5 text-sm font-medium text-gray-200 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:h-10 sm:px-3'
        >
          <CollectionIcon className='h-4 w-4 sm:hidden' />
          <span className='hidden sm:inline'>{t('Select all')}</span>
          <ChevronUpIcon className='hidden h-4 w-4 opacity-70 sm:block' />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter='transition ease-out duration-150'
          enterFrom='opacity-0 translate-y-1 scale-95'
          enterTo='opacity-100 translate-y-0 scale-100'
          leave='transition ease-in duration-100'
          leaveFrom='opacity-100 scale-100'
          leaveTo='opacity-0 scale-95'
        >
          <Menu.Items className='absolute bottom-full left-0 mb-3 w-64 origin-bottom-left overflow-hidden rounded-xl bg-gray-900/95 p-1.5 text-sm text-gray-100 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl focus:outline-none dark:bg-slate-800/95'>
            {options.map((option) => (
              <Menu.Item key={option.key}>
                {({ active: highlighted }) => (
                  <button
                    type='button'
                    onClick={option.onSelect}
                    className={clsxm(
                      'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left',
                      highlighted && 'bg-white/10 text-white',
                    )}
                  >
                    <span className='truncate'>{option.label}</span>
                    {option.key === 'visible' && (
                      <kbd className='ml-3 rounded bg-white/10 px-1.5 py-0.5 font-sans text-[11px] text-gray-300'>
                        {shortcut}
                      </kbd>
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    );
  };

  const renderIdle = () => {
    const ownOnlyHint =
      count > 0 && ownCount === 0
        ? t('Only your own items can be changed')
        : undefined;
    return (
      <>
        {!context.alwaysSelecting && (
          <BarButton
            onClick={actions.clear}
            label={t('Clear selection')}
            tooltip={t('Clear selection (Esc)')}
            className='w-9 px-0 sm:w-10 sm:px-0'
          >
            <XIcon className='h-5 w-5' />
          </BarButton>
        )}
        <p
          aria-live='polite'
          className='min-w-0 shrink truncate px-1.5 text-sm font-semibold text-white sm:px-2'
        >
          {count > 0 ? (
            <span className='tabular-nums'>
              {t('{{count}} selected', { count })}
            </span>
          ) : mode === 'trash' ? (
            t('Select items to restore or delete')
          ) : (
            t('Select items')
          )}
        </p>
        {renderSelectMenu()}
        <div className='ml-auto flex items-center gap-0.5 sm:gap-1'>
          <Divider />
          <BarButton
            tone={mode === 'trash' ? 'ghost' : 'primary'}
            disabled={!downloadableCount}
            onClick={() => void actions.downloadSelected()}
            tooltip={
              downloadableCount > DOWNLOAD_BATCH_MAX_ITEMS
                ? t('Downloads in batches of up to {{count}} files', {
                    count: DOWNLOAD_BATCH_MAX_ITEMS,
                  })
                : downloadableCount > 1
                  ? t('Download as ZIP')
                  : mode === 'trash'
                    ? t('Download')
                    : undefined
            }
            label={t('Download')}
            className={
              mode === 'trash' ? 'w-9 px-0 sm:w-10 sm:px-0' : undefined
            }
          >
            <DownloadIcon className='h-4 w-4' />
            {/* In Trash, Restore is the main action; Download is icon-only. */}
            {mode === 'library' && (
              <span className='hidden sm:inline'>{t('Download')}</span>
            )}
          </BarButton>
          {mode === 'library' ? (
            <>
              {/* Icon-only: the label is long in several languages. */}
              <BarButton
                disabled={!ownCount}
                onClick={() => void actions.setFavoriteSelected(!allFavorite)}
                tooltip={
                  ownOnlyHint ?? (allFavorite ? t('Unfavorite') : t('Favorite'))
                }
                label={allFavorite ? t('Unfavorite') : t('Favorite')}
                className='w-9 px-0 sm:w-10 sm:px-0'
              >
                {allFavorite ? (
                  <HeartSolidIcon className='h-4 w-4 text-red-400' />
                ) : (
                  <HeartOutlineIcon className='h-4 w-4' />
                )}
              </BarButton>
              <BarButton
                buttonRef={deleteButtonRef}
                tone='dangerGhost'
                disabled={!ownCount}
                onClick={() => setConfirming(true)}
                tooltip={ownOnlyHint}
                label={t('Delete')}
              >
                <TrashIcon className='h-4 w-4' />
                <span className='hidden sm:inline'>{t('Delete')}</span>
              </BarButton>
            </>
          ) : (
            <>
              <BarButton
                buttonRef={deleteButtonRef}
                tone='dangerGhost'
                disabled={!ownCount}
                onClick={() => setConfirming(true)}
                label={t('Delete forever')}
                className='w-9 px-0 sm:w-auto sm:px-3'
              >
                <TrashIcon className='h-4 w-4' />
                <span className='hidden sm:inline'>{t('Delete forever')}</span>
              </BarButton>
              <BarButton
                tone='primary'
                disabled={!ownCount}
                onClick={() => void actions.restoreSelected()}
                label={t('Restore')}
              >
                <ReplyIcon className='h-4 w-4' />
                <span>{t('Restore')}</span>
              </BarButton>
            </>
          )}
        </div>
      </>
    );
  };

  const content = task
    ? renderTask(task)
    : confirming
      ? renderConfirm()
      : renderIdle();

  return createPortal(
    <div
      className='pointer-events-none fixed bottom-[calc(var(--mobile-tab-bar-height,0px)+1rem)] z-[60] flex justify-center px-3 sm:bottom-[calc(var(--mobile-tab-bar-height,0px)+1.5rem)]'
      style={
        anchor
          ? { left: anchor.left, width: anchor.width }
          : { left: 0, right: 0 }
      }
    >
      <Transition
        show={visible}
        as={Fragment}
        enter='transition duration-300 ease-out'
        enterFrom='translate-y-6 opacity-0 scale-[0.98]'
        enterTo='translate-y-0 opacity-100 scale-100'
        leave='transition duration-200 ease-in'
        leaveFrom='translate-y-0 opacity-100'
        leaveTo='translate-y-6 opacity-0'
      >
        <div
          role='toolbar'
          aria-label={t('Selection actions')}
          translate='no'
          className='notranslate pointer-events-auto relative flex w-full max-w-[44rem] items-center gap-0.5 rounded-2xl bg-gray-900/95 p-1.5 text-white shadow-2xl shadow-gray-900/25 ring-1 ring-white/10 backdrop-blur-xl dark:bg-slate-800/95 dark:shadow-black/40 sm:gap-1'
        >
          {content}
        </div>
      </Transition>
    </div>,
    document.body,
  );
};

export default MediaSelectionBar;
